import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Badge, Popover, List, ListItem, ListItemText, ListItemIcon, Typography, Box, alpha } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import FlightIcon from '@mui/icons-material/Flight';
import WarningIcon from '@mui/icons-material/Warning';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from 'hooks/api/notifications/useNotifications';
import { NotificationReadStatus } from 'hooks/api/notifications/reducer';
import { themeColors } from 'views/ConstantColors';

const severityColors = {
    error: {
        main: '#FF0000',
        light: '#FFE6E6',
        bg: alpha('#FFE6E6', 0.5),
        contrastText: '#FFFFFF'
    },
    success: {
        main: '#1C4B45',
        light: '#E6EFED',
        bg: alpha('#E6EFED', 0.5),
        contrastText: '#FFFFFF'
    },
    warning: {
        main: '#FFA500',
        light: '#FFF3E6',
        bg: alpha('#FFF3E6', 0.5),
        contrastText: '#000000'
    },
    info: {
        main: themeColors.primary.main,
        light: themeColors.primary.lighter,
        bg: alpha(themeColors.primary.lighter, 0.5),
        contrastText: themeColors.primary.contrastText
    }
};

const getNotificationSeverity = (content) => {
    const lowerContent = content.toLowerCase();
    if (lowerContent.includes('declined') || lowerContent.includes('rejected')) {
        return 'error';
    }
    if (lowerContent.includes('approved') || lowerContent.includes('completed')) {
        return 'success';
    }
    if (lowerContent.includes('required') || lowerContent.includes('pending')) {
        return 'warning';
    }
    return 'info';
};

const getNotificationIcon = (objectType) => {
    switch (objectType?.toLowerCase()) {
        case 'travelrequest':
            return FlightIcon;
        case 'travelplan':
            return EventIcon;
        case 'acquittal':
            return ReceiptIcon;
        case 'approval':
            return CheckCircleIcon;
        case 'alert':
            return WarningIcon;
        default:
            return NotificationsIcon;
    }
};

const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days === 1 ? '' : 's'} ago`;
    if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    if (minutes > 0) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    return 'Just now';
};

const getNavigationPath = (notification) => {
    if (!notification?.object?.objectType || notification.read) return null;

    const { objectType, id } = notification.object;
    const content = notification.content.toLowerCase();

    if (objectType === 'TravelRequest') {
        if (content.includes('travel request approval required for')) {
            return `/trip-administration-request/${id}`;
        }
        if (content.includes('travel request declined by')) {
            return `/trip/${id}`;
        }
        if (content.includes('travel request approved by')) {
            return `/trip/${id}`;
        }
        return `/trip/${id}`;
    }

    if (objectType === 'Acquittal') {
        if (content.includes('acquittal approval required for')) {
            return `/trip-administration-acquital/${id}`;
        }
        if (content.includes('acquittal declined by')) {
            return `/trip/${id}`;
        }
        if (content.includes('acquittal approved by')) {
            return `/trip/${id}`;
        }
        return `/trip/${id}`;
    }

    return null;
};

const NotificationItem = ({ notification, severityColor, onNotificationClick }) => (
    <ListItem
        onClick={() => !notification.read && onNotificationClick(notification)}
        sx={{
            borderBottom: `1px solid ${themeColors.grey[200]}`,
            backgroundColor: notification.read ? 'transparent' : severityColor.bg,
            padding: '16px',
            opacity: notification.read ? 0.7 : 1,
            '&:hover': {
                backgroundColor: notification.read ? 'transparent' : alpha(severityColor.light, 0.2),
                cursor: notification.read ? 'default' : 'pointer',
                transform: notification.read ? 'none' : 'translateX(4px)'
            },
            transition: 'all 0.2s ease'
        }}
    >
        <ListItemIcon>
            {React.createElement(getNotificationIcon(notification.object?.objectType), {
                sx: {
                    color: notification.read ? themeColors.grey[400] : severityColor.main,
                    fontSize: '1.5rem'
                }
            })}
        </ListItemIcon>
        <ListItemText
            primary={notification.content}
            secondary={formatTimeAgo(notification.createdAt)}
            primaryTypographyProps={{
                sx: {
                    fontSize: '0.9rem',
                    fontWeight: notification.read ? 'normal' : 600,
                    color: notification.read ? themeColors.text.primary : severityColor.main,
                    mb: 0.5
                }
            }}
            secondaryTypographyProps={{
                sx: {
                    fontSize: '0.8rem',
                    color: themeColors.text.secondary
                }
            }}
        />
    </ListItem>
);

NotificationItem.propTypes = {
    notification: PropTypes.shape({
        id: PropTypes.number.isRequired,
        content: PropTypes.string.isRequired,
        read: PropTypes.bool.isRequired,
        createdAt: PropTypes.string.isRequired,
        object: PropTypes.shape({
            id: PropTypes.string,
            objectType: PropTypes.string,
            name: PropTypes.string,
            summary: PropTypes.string,
            description: PropTypes.string
        })
    }).isRequired,
    severityColor: PropTypes.shape({
        main: PropTypes.string.isRequired,
        light: PropTypes.string.isRequired,
        bg: PropTypes.string.isRequired,
        contrastText: PropTypes.string.isRequired
    }).isRequired,
    onNotificationClick: PropTypes.func.isRequired
};

const NotificationBell = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { notifications, loading, fetchNotifications, markAsRead } = useNotifications();
    const [unreadCount, setUnreadCount] = useState(0);
    const [hasShownInitial, setHasShownInitial] = useState(false);
    const buttonRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                await fetchNotifications(NotificationReadStatus.ALL, { page: 0, size: 200 });
            } catch (error) {
                console.error('Error fetching notifications:', error);
            }
        };

        fetchData();
        const pollInterval = setInterval(fetchData, 60000);

        return () => clearInterval(pollInterval);
    }, [fetchNotifications]);

    useEffect(() => {
        let showTimer;
        let closeTimer;

        if (notifications) {
            const count = notifications.filter((notification) => !notification.read).length;
            setUnreadCount(count);

            if (count > 0 && !hasShownInitial) {
                showTimer = setTimeout(() => {
                    setAnchorEl(buttonRef.current);
                    setHasShownInitial(true);

                    closeTimer = setTimeout(() => {
                        setAnchorEl(null);
                    }, 3000);
                }, 1000);
            }
        }

        return () => {
            if (showTimer) clearTimeout(showTimer);
            if (closeTimer) clearTimeout(closeTimer);
        };
    }, [notifications, hasShownInitial]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNavigation = (notification) => {
        const path = getNavigationPath(notification);
        if (path) {
            handleClose();
            navigate(path);
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.read) {
            try {
                await markAsRead(notification.id);
                await fetchNotifications(NotificationReadStatus.ALL, { page: 0, size: 20 });

                setTimeout(() => {
                    handleNavigation(notification);
                }, 1000);
            } catch (error) {
                console.error('Error handling notification click:', error);
            }
        }
    };

    const sortedNotifications = notifications?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return (
        <Box sx={{ mx: 1 }}>
            <IconButton
                ref={buttonRef}
                onClick={handleClick}
                size="large"
                aria-label={`show ${unreadCount} new notifications`}
                color="inherit"
                sx={{
                    position: 'relative',
                    color: themeColors.primary.main,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: alpha(themeColors.primary.main, 0.08),
                        transform: 'translateY(-1px)'
                    }
                }}
            >
                <Badge
                    badgeContent={unreadCount}
                    sx={{
                        '& .MuiBadge-badge': {
                            backgroundColor: themeColors.primary.main,
                            color: themeColors.primary.contrastText,
                            fontWeight: 'bold',
                            minWidth: '20px',
                            height: '20px',
                            borderRadius: '10px',
                            border: unreadCount > 0 ? `2px solid ${themeColors.primary.contrastText}` : 'none'
                        }
                    }}
                >
                    <NotificationsIcon sx={{ fontSize: '1.5rem' }} />
                </Badge>
            </IconButton>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                PaperProps={{
                    sx: {
                        width: 360,
                        maxHeight: {
                            xs: 'calc(100vh - 100px)', // For mobile screens
                            sm: 480 // For larger screens
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: `0 4px 12px ${alpha(themeColors.primary.main, 0.15)}`,
                        border: `1px solid ${alpha(themeColors.primary.main, 0.1)}`,
                        borderRadius: '12px'
                    }
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        borderBottom: `1px solid ${themeColors.grey[200]}`,
                        backgroundColor: severityColors.info.light,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                    }}
                >
                    <Typography variant="h6" sx={{ color: severityColors.info.main, fontWeight: 600 }}>
                        Notifications
                    </Typography>
                    {unreadCount > 0 && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: themeColors.primary.main,
                                backgroundColor: alpha(themeColors.primary.main, 0.1),
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontWeight: 500
                            }}
                        >
                            {unreadCount} new
                        </Typography>
                    )}
                </Box>

                <List
                    sx={{
                        p: 0,
                        overflowY: 'auto',
                        flex: 1,
                        '&::-webkit-scrollbar': {
                            width: '8px'
                        },
                        '&::-webkit-scrollbar-track': {
                            background: themeColors.grey[100]
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: themeColors.grey[300],
                            borderRadius: '4px',
                            '&:hover': {
                                background: themeColors.grey[400]
                            }
                        }
                    }}
                >
                    {loading && (
                        <ListItem>
                            <ListItemText primary="Loading notifications..." sx={{ textAlign: 'center', color: themeColors.grey[600] }} />
                        </ListItem>
                    )}

                    {!loading && (!sortedNotifications || sortedNotifications.length === 0) && (
                        <ListItem>
                            <ListItemText primary="No notifications" sx={{ textAlign: 'center', color: themeColors.grey[600] }} />
                        </ListItem>
                    )}

                    {!loading &&
                        sortedNotifications?.map((notification) => {
                            const severity = getNotificationSeverity(notification.content);
                            return (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    severityColor={severityColors[severity]}
                                    onNotificationClick={handleNotificationClick}
                                />
                            );
                        })}
                </List>
            </Popover>
        </Box>
    );
};

export default NotificationBell;
